'use client'
import React, { useEffect } from 'react'
// Scoped to `.client-portal-root` so auth pages that lazy-load ClientLayoutServer
// never leak client typography resets onto patient portal routes.
import './client-portal.css'
import NavbarGate from '@/components/client/navigation/NavbarGate'
import {
  isClientAuthPath,
  isClientPreAuthHeaderPath,
  shouldShowClientPortalFooter,
  useClientNavMode,
} from '@/components/client/navigation/clientNavMode'
import { usePathname } from 'next/navigation'
import { ClientSelectionProvider } from '@/providers/ClientSelectionContext'
import {
  ClientPortalContextProvider,
  useClientPortalContext,
} from '@/providers/ClientPortalContext'
import { ReactQueryProvider } from '@/providers/ReactQueryProvider'
import { useStytchMember } from '@stytch/nextjs/b2b'
import Loader from '@/components/client/Loader'
import { Footer } from '@/Footer/Component'
import { TenantContext } from '@/components/TenantLayout/Client'
import { KitImageProvider } from '@/hooks/useKitImage'
import type { Tenant } from '@/payload-types'
import type { StytchUser } from '@/utilities/stytch/server'
import { cn } from '@/utilities/ui'
import { clearTenantThemeStyles } from '@/utilities/tenantStyles'

interface ClientLayoutClientProps {
  children: React.ReactNode
  tenant: Tenant | null
  user: StytchUser | null
  className?: string
  contentClassName?: string
}

function ClientPortalRouteContent({ children }: Readonly<{ children: React.ReactNode }>) {
  const { isProgramScopedRouteRedirecting } = useClientPortalContext()
  if (isProgramScopedRouteRedirecting) {
    return <Loader testId="client-program-context-redirect-loader" />
  }
  return children
}

export default function ClientLayoutClient({
  children,
  tenant,
  user,
  className,
  contentClassName,
}: Readonly<ClientLayoutClientProps>) {
  const pathname = usePathname() || ''
  const { isInitialized } = useStytchMember()
  const navMode = useClientNavMode()
  const isAuthRoute = isClientAuthPath(pathname)
  const showInitLoader = !isInitialized && !isClientPreAuthHeaderPath(pathname) && !isAuthRoute
  const showFooter = shouldShowClientPortalFooter(pathname)

  useEffect(() => {
    clearTenantThemeStyles()
  }, [])

  return (
    <ClientSelectionProvider>
      <ReactQueryProvider>
        <ClientPortalContextProvider enabled={navMode === 'post-auth'}>
          <TenantContext.Provider value={{ tenant, user, isOnboarding: false }}>
            <KitImageProvider>
              <div
                className={cn(
                  'client-portal-root relative flex h-full min-h-0 flex-col',
                  className,
                )}
              >
                <NavbarGate />
                {showInitLoader && (
                  <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/70 backdrop-blur-sm">
                    <Loader testId="client-layout-loading" />
                  </div>
                )}
                <div
                  className={cn(
                    'min-h-0 flex-1',
                    isAuthRoute ? 'overflow-hidden' : 'overflow-auto',
                    navMode === 'post-auth' && 'pt-18 pb-18 sm:pb-0 sm:pt-16',
                    contentClassName,
                  )}
                >
                  {/* Grow with content so the footer stays after tall pages, not mid-viewport. */}
                  <div className={cn('flex min-h-full flex-col', isAuthRoute && 'h-full min-h-0')}>
                    <div className={cn('flex flex-1 flex-col', isAuthRoute && 'min-h-0')}>
                      <ClientPortalRouteContent>{children}</ClientPortalRouteContent>
                    </div>
                    {showFooter ? <Footer /> : null}
                  </div>
                </div>
              </div>
            </KitImageProvider>
          </TenantContext.Provider>
        </ClientPortalContextProvider>
      </ReactQueryProvider>
    </ClientSelectionProvider>
  )
}
